import { memo, type ReactNode } from "react";
import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

export type FluxZapQuickActionCardSize = "default" | "compact";

export interface FluxZapQuickActionCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  /** Cor de fundo do avatar (token do tema MUI). */
  avatarBgColor: string;
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  size?: FluxZapQuickActionCardSize;
}

const SIZE_STYLES = {
  default: {
    avatar: { width: 64, height: 64, mb: 1.5 },
    iconFontSize: 32,
    titleVariant: "subtitle1" as const,
    descriptionVariant: "body2" as const,
    minHeight: { xs: 160, sm: 180 },
    cardRadius: 3,
    actionPadding: 2,
  },
  compact: {
    avatar: { width: 40, height: 40, mb: 1 },
    iconFontSize: 22,
    titleVariant: "subtitle2" as const,
    descriptionVariant: "caption" as const,
    minHeight: { xs: 96, sm: 108 },
    cardRadius: 2,
    actionPadding: 1.25,
  },
};

/**
 * Card de ação rápida FluxZap — visual alinhado ao TypeSelector (default ou compact).
 */
const FluxZapQuickActionCard = memo(
  ({
    title,
    description,
    icon,
    avatarBgColor,
    onClick,
    disabled = false,
    ariaLabel,
    size = "default",
  }: FluxZapQuickActionCardProps) => {
    const styles = SIZE_STYLES[size];

    return (
      <Card
        sx={{
          flex: 1,
          minWidth: 0,
          borderRadius: styles.cardRadius,
          boxShadow:
            size === "compact"
              ? "0px 2px 8px rgba(0, 0, 0, 0.08)"
              : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
          opacity: disabled ? 0.55 : 1,
          "&:hover": disabled
            ? undefined
            : {
                transform: "translateY(-2px)",
                boxShadow:
                  size === "compact"
                    ? "0px 4px 14px rgba(0, 0, 0, 0.12)"
                    : "0px 8px 20px rgba(0, 0, 0, 0.15)",
              },
        }}
      >
        <CardActionArea
          onClick={disabled ? undefined : onClick}
          disabled={disabled}
          aria-label={ariaLabel}
          sx={{
            p: styles.actionPadding,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: styles.minHeight,
          }}
        >
          <CardContent sx={{ textAlign: "center", width: "100%", p: 0.5 }}>
            <Avatar
              sx={{
                ...styles.avatar,
                mx: "auto",
                bgcolor: avatarBgColor,
                color: "white",
              }}
            >
              {icon}
            </Avatar>
            <Typography
              variant={styles.titleVariant}
              fontWeight={600}
              gutterBottom={size === "default"}
              sx={{ lineHeight: 1.25 }}
            >
              {title}
            </Typography>
            <Typography
              variant={styles.descriptionVariant}
              color="text.secondary"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: size === "compact" ? 2 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {description}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  },
);

FluxZapQuickActionCard.displayName = "FluxZapQuickActionCard";

export default FluxZapQuickActionCard;
